import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { fetchStores } from "../store/actions/storeActions";
import { getCategories } from "../store/actions/categoryActions";
import { buildCategoryChips } from "../utils/categoryChips";
import StoresHero from "../components/stores/StoresHero";
import StoresAlphabetNav from "../components/stores/StoresAlphabetNav";
import StoresCategoryChips from "../components/stores/StoresCategoryChips";
import StoresResultsBlock from "../components/stores/StoresResultsBlock";
import useSEO from "../hooks/useSEO";
import { generateCollectionPageSchema } from "../utils/schemaGenerators";
import seoConfig from "../config/seoConfig";

const Body = styled.div`
  padding: 1.5rem 0 2rem;
`;

const ErrorNote = styled.p`
  text-align: center;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: hsl(0 84% 60%);
`;

export default function StoresPage() {
  const dispatch = useDispatch();
  const reduxStores = useSelector((s) => s.store.stores) || [];
  const storeError = useSelector((s) => s.store.error);
  const storeLoading = useSelector((s) => s.store.loading);
  const reduxCategories = useSelector((s) => s.category.categories);

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLetter, setFilterLetter] = useState(null);

  useEffect(() => {
    dispatch(fetchStores());
    dispatch(getCategories());
  }, [dispatch]);

  const categoryChips = useMemo(
    () => buildCategoryChips(reduxCategories, reduxStores),
    [reduxCategories, reduxStores]
  );

  const filtered = useMemo(() => {
    let list =
      selectedCategory === "All"
        ? reduxStores
        : reduxStores.filter((s) => s.category === selectedCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q));
    }
    if (filterLetter) {
      if (filterLetter === "#") {
        list = list.filter((s) => /^[^A-Za-z]/.test(s.name));
      } else {
        list = list.filter((s) =>
          s.name.toUpperCase().startsWith(filterLetter)
        );
      }
    }
    return list;
  }, [selectedCategory, searchQuery, filterLetter, reduxStores]);

  const clearAll = () => {
    setSearchQuery("");
    setFilterLetter(null);
    setSelectedCategory("All");
  };

  const seo = useSEO({
    schema: generateCollectionPageSchema({
      name: "All Stores",
      description: "Browse coupons and deals from partner stores.",
      url: `${seoConfig.siteUrl}/stores`,
      numberOfItems: reduxStores.length,
    }),
  });

  return (
    <>
      {seo}
      <StoresHero
        storeCount={reduxStores.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
      <Body>
        {storeError && <ErrorNote>{storeError}</ErrorNote>}
        <StoresAlphabetNav
          filterLetter={filterLetter}
          onFilterLetter={setFilterLetter}
        />
        <StoresCategoryChips
          categories={categoryChips}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
        />
        <StoresResultsBlock
          filtered={filtered}
          searchQuery={searchQuery}
          filterLetter={filterLetter}
          onClearFilters={clearAll}
          loading={storeLoading}
        />
      </Body>
    </>
  );
}
